import { Component } from "@theme/component";

const SVG_NS = "http://www.w3.org/2000/svg";
const VIEWBOX_W = 362;
const VIEWBOX_H = 6;
const STAGGER_SECONDS = 0.1;
const SQUIGGLE_PATH =
  "M19.4121 3.62402C68.5119 1.96055 117.642 0.720871 166.771 1.05469H166.771C174.998 1.10458 183.288 1.18556 191.557 1.27832V1.2793C241.884 1.87206 292.278 2.75633 342.59 3.6709C348.516 3.78003 354.724 3.89556 360.509 4.00586C360.641 4.0084 360.768 4.06356 360.86 4.15918C360.952 4.25479 361.002 4.38273 361 4.51465C360.998 4.61405 360.966 4.71027 360.91 4.79102L360.847 4.86621C360.751 4.95825 360.622 5.00738 360.49 5.00488C354.707 4.8946 348.497 4.78003 342.571 4.6709C292.264 3.7564 241.864 2.87194 191.547 2.2793H191.546C183.279 2.18656 174.989 2.10456 166.767 2.05469C117.646 1.72116 68.5596 2.9598 19.4346 4.62402C13.441 4.7986 7.49666 4.98871 1.49902 5.00488C1.36714 5.00462 1.24024 4.95308 1.14648 4.85938C1.05263 4.76552 1.00006 4.63819 1 4.50586L1.00977 4.4082C1.02893 4.31217 1.07598 4.22289 1.14648 4.15234C1.24045 4.05838 1.36767 4.00586 1.5 4.00586H1.50293C7.44921 3.9901 13.4333 3.79822 19.4072 3.62402H19.4121Z";

function createLineDecoration() {
  const wrap = document.createElement("span");
  wrap.className = "link-decoration";

  const svg = document.createElementNS(SVG_NS, "svg");
  svg.setAttribute("viewBox", `0 0 ${VIEWBOX_W} ${VIEWBOX_H}`);
  svg.setAttribute("preserveAspectRatio", "none");
  svg.setAttribute("aria-hidden", "true");
  svg.setAttribute("focusable", "false");

  const path = document.createElementNS(SVG_NS, "path");
  path.setAttribute("d", SQUIGGLE_PATH);
  path.setAttribute("preserve", SQUIGGLE_PATH);
  path.setAttribute("fill", "#477DFD");
  path.setAttribute("stroke", "#477DFD");
  path.setAttribute("stroke-width", "1.2");
  path.setAttribute("stroke-linejoin", "round");
  path.setAttribute("stroke-linecap", "round");
  path.setAttribute("vector-effect", "non-scaling-stroke");

  svg.appendChild(path);
  wrap.appendChild(svg);
  return wrap;
}

/**
 * Resolves the element whose text should be measured & decorated.
 * Prefers the existing `.reversed-link__text` span (which the theme uses to
 * isolate the underlined text from icons and other inline content). Falls
 * back to the `.reversed-link` itself when no such span exists.
 *
 * @param {HTMLElement} link
 * @returns {HTMLElement}
 */
function resolveTarget(link) {
  return link.querySelector(".reversed-link__text") || link;
}

/**
 * @typedef {{ left: string, top: string, width: string, height: string, delay: string }} LinePlan
 * @typedef {{ lines: LinePlan[], key: string }} Plan
 */

/**
 * <link-item-decoration>: the squiggle under each line of a `.reversed-link`'s text.
 *
 * Every decoration on the page shares one ResizeObserver (on the text it decorates) and one pass a
 * frame, which measures every decoration that changed before it draws any of them, and draws only
 * those whose lines moved. Each one measuring and drawing in its own frame callback forced a
 * layout per link as the page started, and again as each one drew.
 */
class LinkItemDecoration extends Component {
  /** @type {Set<LinkItemDecoration>} Decorations to measure and draw in the next pass */
  static #dirty = new Set();
  /** @type {WeakMap<Element, LinkItemDecoration>} Each text observed -> its decoration */
  static #byTarget = new WeakMap();
  /** @type {ResizeObserver | undefined} */
  static #observer;
  static #frame = 0;

  /** @type {HTMLElement | null} */
  #target = null;
  /** @type {string | null} The lines it last drew, as a key */
  #drawn = null;

  connectedCallback() {
    super.connectedCallback();

    const target = this.parentElement;
    if (!target) return;
    this.#target = target;

    LinkItemDecoration.#byTarget.set(target, this);
    // Its first report, once the text is drawn, has it drawn too
    LinkItemDecoration.#observer ??= new ResizeObserver(LinkItemDecoration.#onResize);
    LinkItemDecoration.#observer.observe(target);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const target = this.#target;
    if (target) {
      LinkItemDecoration.#observer?.unobserve(target);
      if (LinkItemDecoration.#byTarget.get(target) === this) LinkItemDecoration.#byTarget.delete(target);
    }
    LinkItemDecoration.#dirty.delete(this);
    this.#target = null;
    this.#drawn = null;
  }

  updatedCallback() {
    super.updatedCallback();
    this.#drawn = null;
    LinkItemDecoration.#schedule(this);
  }

  /** @param {ResizeObserverEntry[]} entries */
  static #onResize = (entries) => {
    for (const { target } of entries) {
      const decoration = LinkItemDecoration.#byTarget.get(target);
      if (decoration) LinkItemDecoration.#schedule(decoration);
    }
  };

  /** @param {LinkItemDecoration} decoration */
  static #schedule(decoration) {
    LinkItemDecoration.#dirty.add(decoration);
    LinkItemDecoration.#frame ||= requestAnimationFrame(LinkItemDecoration.#pass);
  }

  static #pass = () => {
    LinkItemDecoration.#frame = 0;
    const decorations = [...LinkItemDecoration.#dirty];
    LinkItemDecoration.#dirty.clear();
    // Every one is measured before any is drawn
    const plans = decorations.map((decoration) => /** @type {const} */ ([decoration, decoration.#measure()]));
    for (const [decoration, plan] of plans) decoration.#draw(plan);
  };

  /**
   * Where each line of the text runs: reads only.
   * @returns {Plan | null} Its lines, none when the text isn't drawn; null when there's nothing to do
   */
  #measure() {
    const target = this.#target;
    if (!target || !this.isConnected) return null;

    const targetRect = target.getBoundingClientRect();
    if (targetRect.width === 0 || targetRect.height === 0) return { lines: [], key: "" };

    // Collect per-line bounding rects of the target's text content,
    // excluding ourselves from the range so the decoration doesn't pollute
    // the measurement.
    const rects = [];
    for (const child of target.childNodes) {
      if (child === this) continue;
      const range = document.createRange();
      try {
        range.selectNode(child);
      } catch {
        continue;
      }
      for (const r of range.getClientRects()) {
        if (r.width > 0 && r.height > 0) rects.push(r);
      }
    }

    // Merge rects that share a visual line (close bottom edges).
    rects.sort((a, b) => a.top - b.top || a.left - b.left);
    /** @type {{left:number,top:number,right:number,bottom:number}[]} */
    const lines = [];
    for (const r of rects) {
      const last = lines[lines.length - 1];
      if (last && Math.abs(r.bottom - last.bottom) < 2) {
        last.left = Math.min(last.left, r.left);
        last.right = Math.max(last.right, r.right);
        last.top = Math.min(last.top, r.top);
        last.bottom = Math.max(last.bottom, r.bottom);
      } else {
        lines.push({ left: r.left, top: r.top, right: r.right, bottom: r.bottom });
      }
    }

    const fontSize = parseFloat(getComputedStyle(target).fontSize) || 16;
    const decorationHeight = Math.max(4, fontSize * 0.35);
    const stagger = lines.length > 1 ? STAGGER_SECONDS : 0;

    /** @type {LinePlan[]} */
    const plan = [];
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const width = line.right - line.left;
      if (width < 4) continue;
      const left = line.left - targetRect.left;
      const top = line.bottom - targetRect.top - decorationHeight * 0.5;
      plan.push({
        left: `${left}px`,
        top: `${top}px`,
        width: `${width}px`,
        height: `${decorationHeight}px`,
        delay: `${i * stagger}s`,
      });
    }
    return { lines: plan, key: plan.map((line) => Object.values(line).join(" ")).join("|") };
  }

  /** @param {Plan | null} plan From #measure: drawn unless it's what's there */
  #draw(plan) {
    if (!plan || plan.key === this.#drawn) return;
    this.#drawn = plan.key;
    this.replaceChildren(
      ...plan.lines.map((line) => {
        const wrap = createLineDecoration();
        wrap.style.left = line.left;
        wrap.style.top = line.top;
        wrap.style.width = line.width;
        wrap.style.height = line.height;
        wrap.style.setProperty("--link-decoration-delay", line.delay);
        return wrap;
      })
    );
  }
}

if (!customElements.get("link-item-decoration")) {
  customElements.define("link-item-decoration", LinkItemDecoration);
}

function ensureDecoration(link) {
  if (link.dataset.linkDecorated === "1") return;
  const target = resolveTarget(link);
  if (target.querySelector(":scope > link-item-decoration")) {
    link.dataset.linkDecorated = "1";
    return;
  }
  const decoration = document.createElement("link-item-decoration");
  decoration.setAttribute("aria-hidden", "true");
  target.appendChild(decoration);
  link.dataset.linkDecorated = "1";
}

function scan(root) {
  if (!root) return;
  if (root.nodeType === Node.ELEMENT_NODE && root.matches?.(".reversed-link")) {
    ensureDecoration(root);
  }
  const links = root.querySelectorAll?.(".reversed-link");
  if (!links) return;
  for (const link of links) ensureDecoration(link);
}

function start() {
  scan(document.body);
  new MutationObserver((mutations) => {
    for (const m of mutations) {
      // What a decoration adds (itself, and the lines it draws) holds no link
      if (m.target instanceof LinkItemDecoration) continue;
      for (const node of m.addedNodes) {
        if (node.nodeType !== Node.ELEMENT_NODE || node instanceof LinkItemDecoration) continue;
        // A text change, an icon or a badge: no link in it, so no query
        if (!node.firstElementChild && !node.classList.contains("reversed-link")) continue;
        scan(node);
      }
    }
  }).observe(document.body, { childList: true, subtree: true });
}

const supportsHover =
  window.matchMedia?.("(hover: hover) and (pointer: fine)").matches ?? false;

if (supportsHover) {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start, { once: true });
  } else {
    start();
  }
}
